import { lights, uniform } from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { AmbienceDef } from '../../engine/audio/Ambience';
import { CaveGlow } from '../../engine/caves/CaveGlow';
import { CaveInterior } from '../../engine/caves/CaveInterior';
import { CaveMesher } from '../../engine/caves/CaveMesher';
import { CaveField } from '../../engine/caves/caveField';
import { BIG_CAVE, type BuiltCave, buildFixture, SMALL_CAVE } from '../../engine/caves/caveFixtures';
import { type CaveLayout, gateClips, generateInterior, strataOf } from '../../engine/caves/layout';
import type { V3 } from '../../engine/caves/sdf';
import type { PlayerController } from '../../engine/character/PlayerController';
import { PLAYER_CENTER_HEIGHT } from '../../engine/character/PlayerController';
import { flags } from '../../engine/core/flags';
import { GravityField } from '../../engine/gravity/GravityField';
import type { PhysicsWorld } from '../../engine/physics/PhysicsWorld';
import { createInteriorMaterial } from '../../engine/render/materials/caveInteriorMaterial';
import type { RenderOrigin } from '../../engine/render/RenderOrigin';
import type { HollowDef } from '../../engine/universe/HollowDef';
import { buildTube } from '../../engine/universe/Tunnel';
import type { GameWorld } from './GameWorld';

const LAB_AMBIENCE: AmbienceDef = {
  wind: { gain: 0.05, cutoff: 220 },
  drone: { freqs: [49, 73.4], gain: 0.014 },
  events: [{ kind: 'drip', rate: 0.45, gain: 0.06 }],
  echo: 0.55,
};

/**
 * A generated cave on its own (?scene=cavelab), for looking at and walking through the
 * interiors without loading any world: the two entrances' lower turns and the inside
 * between their gates, with the cave's own gravity. `?caveseed=N` picks another cave,
 * `?cavesize=small` a Geodes-sized one.
 */
export class CaveLab implements GameWorld {
  readonly name: string;
  readonly hollows: HollowDef[];
  readonly field = new GravityField();
  readonly ambience = LAB_AMBIENCE;
  readonly currentExposure = 1.5;
  readonly viewRadius = 300;
  readonly hints = [
    'A generated cave on its own. Every wall is a floor: walk up it.',
    '?caveseed=N shows another cave, ?cavesize=small a short one.',
  ];
  readonly layout: CaveLayout;
  readonly fixture: BuiltCave;
  readonly cave: CaveField;
  readonly interior: CaveInterior;
  readonly glow: CaveGlow;
  private readonly lantern = new THREE.PointLight(0xffe0bc, 22, 90, 2);
  private readonly originRel = uniform(new THREE.Vector3());
  private readonly mesher = new CaveMesher();
  private readonly entrances: { anchor: THREE.Vector3; data: ReturnType<typeof buildTube> }[] = [];

  constructor(
    private readonly origin: RenderOrigin,
    scene: THREE.Scene,
  ) {
    const small = flags.str('cavesize', 'big') === 'small';
    const seed = flags.num('caveseed', 1);
    this.name = `Cave lab (${small ? 'small' : 'big'}, seed ${seed})`;
    this.fixture = buildFixture(small ? SMALL_CAVE : BIG_CAVE, seed);
    const { a, b, ends, spec } = this.fixture;
    this.hollows = [a, b];
    this.layout = generateInterior(spec);
    const t0 = performance.now();
    const pullAt = (k: 0 | 1) => {
      const sp = this.layout.routeSpace[k === 0 ? 0 : this.layout.routeSpace.length - 1];
      return this.layout.shape.voids[this.layout.spaces[sp].void].pull ?? { s: 0, a: 1 };
    };
    this.cave = new CaveField(this.layout, this.fixture.origin, [
      { entrance: ends[0], pull: pullAt(0) },
      { entrance: ends[1], pull: pullAt(1) },
    ]);
    console.info(
      `[innerworlds] cave lab: ${this.layout.spaces.map((s) => s.kind).join(', ')}; layout ${(performance.now() - t0).toFixed(0)} ms`,
    );
    const gap = (small ? SMALL_CAVE : BIG_CAVE).gap;
    for (const h of [a, b])
      this.field.add({ center: h.center, radius: h.radius, strength: h.gravity, reach: gap });
    this.field.addRegion(this.cave);

    scene.add(this.lantern);
    const lanterns = lights([this.lantern]);
    const material = createInteriorMaterial({
      originRel: this.originRel,
      gateA: spec.gates[0].p,
      gateB: spec.gates[1].p,
      glowA: new THREE.Color(0.7, 0.46, 0.18),
      glowB: new THREE.Color(0.16, 0.55, 1.3),
      strata: strataOf(spec),
      lights: lanterns,
    });

    // The entrances' lower turns (the part under the ground), as plain tubes.
    for (const e of ends) {
      const path = e.path.slice(e.helixStart);
      const anchor = path[Math.floor(path.length / 2)].clone();
      const data = buildTube(path, e.radius, 28, anchor);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
      geo.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
      geo.setAttribute(
        'aLook',
        new THREE.BufferAttribute(
          new Float32Array((data.positions.length / 3) * 4).map((_, i) => [1, 0.95, 0.9, 0.25][i % 4]),
          4,
        ),
      );
      geo.setIndex(new THREE.BufferAttribute(data.indices, 1));
      geo.computeBoundingSphere();
      const mesh = new THREE.Mesh(geo, material);
      scene.add(mesh);
      origin.anchor(mesh, anchor);
      this.entrances.push({ anchor, data: buildTube(path, e.radius, 20, anchor) });
    }

    const small2 = small;
    this.interior = new CaveInterior(
      `lab-${seed}-${small2 ? 's' : 'b'}`,
      { shape: this.layout.shape, clips: this.clips(), cell: small ? 0.4 : 0.5, n: 32 },
      this.fixture.origin,
      material,
      this.mesher,
      origin,
    );
    scene.add(this.interior.group);
    this.glow = new CaveGlow(lanterns);
    this.interior.glow = this.glow;
    this.interior.group.add(this.glow.group);
    origin.anchor(this.glow.lights, this.fixture.origin);
    this.interior.load(spec.gates[0].p);
  }

  get lightGroups(): THREE.Object3D[] {
    return [this.glow.lights];
  }

  /** The gates, for cutting the interior where the entrances take over. */
  private clips() {
    return gateClips(this.fixture.spec);
  }

  initPhysics(physics: PhysicsWorld): void {
    for (const e of this.entrances) physics.addStaticMesh(e.anchor, e.data.positions, e.data.indices);
    this.interior.setPhysics(physics);
  }

  async spawn(player: PlayerController): Promise<void> {
    // Wait for the first stretch past gate A to be built.
    for (let i = 0; i < 600 && this.interior.chunksBuilt < 6 && this.interior.state !== 'ready'; i++) {
      this.mesher.pump(8);
      this.interior.update(8);
      await new Promise((r) => setTimeout(r, 16));
    }
    const g = this.fixture.spec.gates[0];
    const e = this.fixture.ends[0];
    const local: V3 = [g.p[0] + g.dir[0] * 4, g.p[1] + g.dir[1] * 4, g.p[2] + g.dir[2] * 4];
    const at = new THREE.Vector3(...local).add(this.fixture.origin);
    // Stand on the floor (the side away from world A), facing on into the cave.
    at.addScaledVector(e.down, e.radius - PLAYER_CENTER_HEIGHT - 0.1);
    player.teleport(at, e.down.clone().negate());
    player.faceTowards(at.clone().addScaledVector(e.gateDir, 10));
  }

  update(_player: PlayerController): void {}

  frame(camera: THREE.PerspectiveCamera, cameraUniverse: THREE.Vector3, _dt: number): void {
    this.origin.toRender(this.fixture.origin, this.originRel.value);
    this.lantern.position.set(0.35, -0.4, -0.2).applyQuaternion(camera.quaternion);
    this.mesher.pump(4);
    this.interior.update(4);
    void cameraUniverse;
  }

  hollowAt(): HollowDef | null {
    return null;
  }

  settled(): boolean {
    return this.interior.state === 'ready';
  }

  loadProgress(): number {
    return this.interior.chunksTotal > 0 ? this.interior.chunksBuilt / this.interior.chunksTotal : 0.5;
  }

  stats(): Record<string, string> {
    const i = this.interior;
    return {
      cave: `${i.state} · ${i.chunksBuilt}${i.chunksTotal ? `/${i.chunksTotal}` : ''} chunks · meshed in ${(i.meshMs / 1000).toFixed(1)} s`,
    };
  }
}
